/*
 * Copyright (c) 2016 Omnigon Communications, LLC. All rights reserved.
 *
 * This software is the confidential and proprietary information of Omnigon Communications, LLC
 * ("Confidential Information"). You shall not disclose such Confidential Information and shall access and use it only
 * in accordance with the terms of the license agreement you entered into with Omnigon Communications, LLC, its
 * subsidiaries, affiliates or authorized licensee. Unless required by applicable law or agreed to in writing, this
 * Confidential Information is provided on an "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either
 * express or implied. See the license agreement for the specific language governing permissions and limitations.
 */

import Component from 'can/component/component';
import template from './search.stache';
import can from 'can';
import appContext from 'scripts/app-context';
import _ from 'lodash';
import googleCustomSearchService from 'scripts/services/google-custom-search';
import deviceUtil from 'scripts/utils/device';

const googleCustomSearchConfig = _.get(window.globalSettings, 'googleCustomSearch', {filters: {}});
const prohibitedTitlePattern = '- The Official Website';

let filters = new can.Map({
	all: {
		value: googleCustomSearchConfig.filters.all,
		startIndex: 1,
		queries: null,
		items: [],
		total: 0,
	},
	videos: {
		value: googleCustomSearchConfig.filters.videos,
		startIndex: 1,
		queries: null,
		items: [],
		total: 0
	},
	articles: {
		value: googleCustomSearchConfig.filters.articles,
		startIndex: 1,
		queries: null,
		items: [],
		total: 0
	},
	photos: {
		value: googleCustomSearchConfig.filters.photos,
		startIndex: 1,
		queries: null,
		items: [],
		total: 0
	}
});

Component.extend({
	tag: 'search-component',
	template: template,
	viewModel: {
		isLoaded: false,
		emptyResult: false,
		searchError: false,
		filters: filters,
		activeFilter: 'all',
		define: {
			paginationSettings: {
				Type: can.Map,
				Value: can.Map
			},
			searchInformation: {
				Type: can.Map,
				Value: can.Map
			},
			getResults: {
				get() {
					let activeFilter = this.attr('activeFilter');

					let filter = this.attr('filters').attr(activeFilter),
						items = filter.attr('items') || null;

					if (!items) {
						this.attr('emptyResult', true);
					} else {
						this.attr('emptyResult', false);
					}

					return items;
				}
			},
			getRequestString: {
				get() {
					return this.attr('successfulRequestString');
				}
			}
		},
		displayFilter: function(total) {
			return parseInt(total) > 0;
		},
		getNumberOfResults: function() {
			let type = this.attr('activeFilter'),
				filters = this.attr('filters');

			return filters.attr(type).attr('total');
		},
		onFilterChange: function(event) {
			this.attr('activeFilter', event.payload.value);
			can.route.attr('startIndex', 1);
			can.route.attr('type', event.payload.value);
		},
		setSearchValue: function(event) {
			let searchValue = can.trim($(event.target).val());
			this.attr('queryString', searchValue);
		},
		getPrefix: function(link) {
			if (link.indexOf('/players/') != -1) {
				return 'PLAYER PROFILE | ';
			} else if (link.indexOf('/watch/') != -1) {
				return 'WATCH | ';
			}
			return '';
		},
		getTitle: function(title) {
			let prohibitedIndex = title.indexOf(prohibitedTitlePattern);
			if (prohibitedIndex != -1) {
				return title.substring(0, prohibitedIndex);
			}
			return title;
		}
	},
	events: {
		inserted: function() {
			can.route.ready();

			let queryString = can.route.attr('q'),
				startIndex = can.route.attr('startIndex'),
				type = can.route.attr('type') || this.viewModel.attr('activeFilter');

			if (startIndex && type) {
				can.route.attr({type: type, startIndex: startIndex});
				this.viewModel.attr({activeFilter: type});
			}

			if (queryString) {
				this.viewModel.attr('queryString', queryString);
			}

			if (startIndex != 1) {
				let filter = this.viewModel.attr('filters').attr(type);
				filter.attr('startIndex', startIndex);
			}

			this.fetchData();

			appContext.model.bind('hamburger-search', (type, query) => {
				can.route.attr('q', query);
				this.viewModel.attr('queryString', query);
				this.fetchData();
			});

			appContext.model.bind('page-changed', (type, newStartIndex) => {
				can.route.attr('startIndex', newStartIndex);
				let activeFilter = this.viewModel.attr('activeFilter');
				let filter = this.viewModel.attr('filters').attr(activeFilter);
				filter.attr('startIndex', newStartIndex);
				this.fetchData([activeFilter]);
			});

			appContext.model.bind('do-search', (type, query) => {
				can.route.attr('q', query);
				this.viewModel.attr('queryString', query);
				this.fetchData();
			});

			this.viewModel.bind('change', (ev, attr, how, newVal, oldVal) => {
				if (attr == 'activeFilter') {
					this.viewModel.attr('filters').each(function(filter, key) {
						filter.attr('startIndex', 1);
					});
					this.setPaginationData();
				}
			});
		},
		setPaginationData: function() {
			let activeFilter = this.viewModel.attr('activeFilter');
			let filter = this.viewModel.attr('filters').attr(activeFilter);

			this.viewModel.attr('paginationSettings', {
				startIndex: filter.attr('startIndex'),
				total: filter.attr('total'),
				queries: filter.attr('queries')
			});
		},
		fetchData: function(types) {
			let googleApiPromises = [],
				startIndex = can.route.attr('startIndex'),
				queryString = this.viewModel.attr('queryString'),
				list = types || ['all', 'videos', 'articles', 'photos'],
				type = can.route.attr('type');

			if (!queryString) {
				return;
			}

			if (!startIndex) {
				can.route.attr('startIndex', 1);
			}

			if (!type) {
				can.route.attr('type', 'all');
			}

			this.viewModel.attr('searchError', false);

			this.viewModel.attr('filters').each((filter, key) => {
				if (list.indexOf(key) != -1) {
					let data = _.assign({},
						{
							q: can.sub('{filters} {query}',
								{
									filters: filter.attr('value'),
									query: queryString
								})
						},
						googleCustomSearchConfig.policyKeys,
						{
							num: 10,
							start: filter.attr('startIndex')
						});

					googleApiPromises.push(googleCustomSearchService.fetch(data, key));
				}

			});

			Promise.all(googleApiPromises).then((results) => {
				results.forEach((result) => {
					if (result && result.data) {
						let filter = this.viewModel.attr('filters').attr(result.filter);

						filter.attr('items', result.data.items);
						filter.attr('startIndex', result.data.queries.request[0].startIndex);
						filter.attr('total', result.data.searchInformation.totalResults);
						filter.attr('queries', result.data.queries);
					}
				});

				if (!this.viewModel.attr('isLoaded')) {
					this.viewModel.attr('isLoaded', true);
				}

				this.viewModel.attr('successfulRequestString', queryString);

				this.setPaginationData();

				if (deviceUtil.isMobile) {
					this.element.find('.s-search-bar input.s-input-text').blur();
				}

			}, (error) => {
				this.viewModel.attr('searchError', true);
			});
		},
		'.s-search-bar .s-clear click': function() {
			this.viewModel.attr('queryString', '');
			can.route.removeAttr('q');
		},
		'.s-search-bar .s-form submit': function(el, ev) {
			ev.preventDefault();
			let queryString = encodeURIComponent(this.viewModel.attr('queryString'));
			if (!queryString) {
				return;
			}

			can.route.attr('startIndex', 1);
			let activeFilter = this.viewModel.attr('activeFilter');
			let filter = this.viewModel.attr('filters').attr(activeFilter);
			filter.attr('startIndex', 1);

			can.route.attr('q', queryString);
			this.fetchData();
		}
	}
});
